// Centralized header configuration hook used by web and plugin packages

import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import { selectUser, selectWallets, selectIsLoggedIn } from '../store/slices/appSlice';
// Analytics: header events tracked via data-track attributes
import { getSocialLinks } from '../utils/userMenuIcons';
import { CreditCard, Info, Settings, Share2, ThumbsUp, User, Flag } from 'lucide-react';
import { isWebBuild } from '../utils';

export interface HeaderConfigParams {
  /** Platform-specific navigation handler for profile page */
  onNavigateToProfile: () => void;
  /** Platform-specific navigation handler for billing page */
  onNavigateToBilling: () => void;
  /** Platform-specific navigation handler for the app-settings page */
  onNavigateToAppSettings?: () => void;
  /** Platform-specific navigation handler for report-problem page */
  onNavigateToReport?: () => void;
  /** Platform-specific handler for login dialog/page */
  onLoginClick: () => void;
  /** Platform-specific handler for register dialog/page */
  onRegisterClick: () => void;
  /** Optional: Custom copy website link handler (defaults to copying ideoz.ai) */
  onCopyWebsiteLink?: () => Promise<void>;
  /** Optional: Custom request upgrade handler (defaults to showing toast) */
  onRequestUpgrade?: () => Promise<void>;
  /** Optional: Show toast notification function */
  showToast?: (options: {
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }) => void;
  /** Optional: Current path for determining active menu items */
  currentPath?: string;
}

export interface HeaderConfig {
  // User data
  user: any;
  points: number;
  isLoggedIn: boolean;

  // Header props
  headerProps: {
    user: any;
    points: number;
    loginLabel: string;
    registerLabel: string;
    onLoginClick: () => void;
    onRegisterClick: () => void;
    onLoginAnalytics: () => void;
    onRegisterAnalytics: () => void;
    onUserClick: () => void;
    className?: string;
  };

  // User menu state
  userMenuOpen: boolean;
  onUserMenuClose: () => void;

  // User menu items
  userMenuItems: Array<{
    id: string;
    label: string;
    icon: any;
    onClick?: () => void;
    href?: string;
    target?: '_blank';
    isActive?: boolean;
    dataTestId?: string;
    dataTrack?: string;
  }>;

  // Social links
  userMenuSocialLinks: Array<{
    id: string;
    name: string;
    url: string;
    icon: JSX.Element;
  }>;

  // Request upgrade
  onRequestUpgrade: () => Promise<void>;
  isRequestingUpgrade: boolean;
}

/**
 * Custom hook that provides centralized header configuration
 *
 * @param params - Platform-specific navigation and handlers
 * @returns Complete header configuration ready to pass to templates
 *
 * @example
 * // In web wrapper
 * const headerConfig = useHeaderConfig({
 *   onNavigateToProfile: () => navigate('/profile'),
 *   onLoginClick: authDialog.openLogin,
 *   onRegisterClick: authDialog.openRegister,
 *   showToast,
 * });
 *
 * // Pass to template
 * <Template {...headerConfig} />
 */
export function useHeaderConfig(params: HeaderConfigParams): HeaderConfig {
  const {
    onNavigateToProfile,
    onNavigateToBilling,
    onLoginClick,
    onRegisterClick,
    onCopyWebsiteLink: customCopyWebsiteLink,
    onRequestUpgrade: customRequestUpgrade,
    showToast = () => {},
    currentPath,
  } = params;

  const dispatch = useAppDispatch();

  // Redux state
  const user = useAppSelector(selectUser);
  const wallets = useAppSelector(selectWallets);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);

  // User menu state
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isRequestingUpgrade, setIsRequestingUpgrade] = useState(false);

  // Calculate user points
  const userPoints = wallets?.find((item: any) => item.hasOwnProperty('Point'))?.['Point'] || 0;

  // User menu handlers
  const handleOpenUserMenu = useCallback(() => {
    setUserMenuOpen(true);
  }, []);

  const handleCloseUserMenu = useCallback(() => {
    setUserMenuOpen(false);
  }, []);

  // Default copy website link handler
  const defaultCopyWebsiteLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText('https://ideoz.ai/');
      showToast({
        message: 'Website link copied',
        severity: 'success',
      });
      // Analytics: ui.share.clicked tracked via data-track attribute
      handleCloseUserMenu();
    } catch (err) {
      console.error('Copy failed', err);
      showToast({
        message: 'Copy failed',
        severity: 'error',
      });
    }
  }, [handleCloseUserMenu, showToast]);

  const copyWebsiteLink = customCopyWebsiteLink || defaultCopyWebsiteLink;

  // Default request upgrade handler
  const defaultRequestUpgrade = useCallback(async () => {
    setIsRequestingUpgrade(true);
    try {
      // TODO: Implement requestUpgrade Redux action
      showToast({
        message: 'Your upgrade request has been submitted successfully!',
        severity: 'success',
      });
      handleCloseUserMenu();
    } catch (error) {
      console.error('Error requesting upgrade:', error);
      showToast({
        message: 'An error occurred while requesting upgrade.',
        severity: 'error',
      });
    } finally {
      setIsRequestingUpgrade(false);
    }
  }, [handleCloseUserMenu, showToast]);

  const handleRequestUpgrade = customRequestUpgrade || defaultRequestUpgrade;

  // User menu items configuration
  const userMenuItems = [
    {
      id: 'profile',
      label: 'Profile',
      icon: User,
      path: '/profile',
      isActive: currentPath === '/profile',
      onClick: () => {
        // Analytics: page.viewed tracked via usePageTracking
        onNavigateToProfile();
        handleCloseUserMenu();
      },
    },
    {
      id: 'billing',
      label: 'Billing',
      icon: CreditCard,
      path: '/billing',
      isActive: currentPath === '/billing',
      onClick: () => {
        onNavigateToBilling();
        handleCloseUserMenu();
      },
      dataTestId: 'user-menu-item-billing',
      dataTrack: 'ui.billing.open',
    },
    {
      id: 'app_settings',
      label: 'App Settings',
      icon: Settings,
      path: '/app-settings',
      isActive: currentPath === '/app-settings',
      onClick: () => {
        params.onNavigateToAppSettings?.();
        handleCloseUserMenu();
      },
      dataTestId: 'user-menu-item-app-settings',
      dataTrack: 'ui.app_settings.open',
    },
    {
      id: 'help',
      label: 'Help & Documentation',
      icon: Info,
      href: 'https://ideoz.ai/documentation/',
      target: '_blank' as const,
      onClick: () => {
        // Analytics: ui.help_docs.clicked tracked via data-track attribute
      },
    },
    {
      id: 'report_problem',
      label: 'Report a problem',
      icon: Flag,
      onClick: () => {
        try {
          // Only capture the exact `window.location.href` for Web builds.
          // Plugin environments may not have meaningful location.href and
          // may already write a descriptive platform link; avoid overwriting
          // that value from the plugin header menu.
          if (typeof window !== 'undefined' && isWebBuild()) {
            try {
              sessionStorage.setItem('ideoz_report_source_url', window.location.href);
            } catch (e) {}
          }
        } catch (e) {}
        if (typeof params.onNavigateToReport === 'function') {
          params.onNavigateToReport();
        } else if (typeof window !== 'undefined') {
          // SPA navigate fallback: push state and emit locationchange
          try {
            history.pushState({}, document.title, '/report-problem');
            window.dispatchEvent(new Event('locationchange'));
          } catch (e) {
            // as a last resort, full navigation
            window.location.href = '/report-problem';
          }
        }
        handleCloseUserMenu();
      },
      dataTestId: 'user-menu-item-report-problem',
      dataTrack: 'ui.report_problem.open',
    },
    // isWebBuild() ? {
    //   id: 'share',
    //   label: 'Share',
    //   icon: Share2,
    //   onClick: copyWebsiteLink,
    // } : null,
    // {
    //   id: 'community',
    //   label: 'Join Ideozers ',
    //   icon: ThumbsUp,
    //   href: 'https://www.facebook.com/share/g/1Apr8dMTyu/',
    //   target: '_blank' as const,
    // },
  ].filter((item): item is NonNullable<typeof item> => item !== null);

  // Social links
  const userMenuSocialLinks = getSocialLinks();

  // Header props configuration
  const headerProps = {
    user,
    points: userPoints,
    loginLabel: 'Login',
    registerLabel: 'Create free account',
    onLoginClick,
    onRegisterClick,
    onLoginAnalytics: () => {
      // TODO: Add analytics event
    },
    onRegisterAnalytics: () => {
      // TODO: Add analytics event
    },
    onUserClick: handleOpenUserMenu,
  };

  return {
    // User data
    user,
    points: userPoints,
    isLoggedIn,

    // Header props
    headerProps,

    // User menu state
    userMenuOpen,
    onUserMenuClose: handleCloseUserMenu,

    // User menu items
    userMenuItems,

    // Social links
    userMenuSocialLinks,

    // Request upgrade
    onRequestUpgrade: handleRequestUpgrade,
    isRequestingUpgrade,
  };
}
